import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useBranding } from '../../context/BrandingContext';
import { Button } from '../ui/button';
import { APP_VERSION } from '../../config/version';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { 
  ChevronDown, 
  LogOut, 
  LayoutDashboard,
  Network,
  Monitor,
  UserCog,
  User,
  Box,
  AppWindow,
  Settings,
  FileSpreadsheet,
} from 'lucide-react';

// Import refactored components
import {
  appsItems,
  assetsSections,
  microSections,
  configSections,
  adminSections,
  ipamSections,
  reportsItems,
  MegaMenu,
  MobileNavigation,
} from './navbar';

export const Navbar = () => {
  const { user, logout, canRead, isAdmin } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { branding } = useBranding();
  const location = useLocation();

  const logoSmallUrl = branding.company_logo_small_url || branding.company_logo_url || 'https://www.i4.com.ar/images/i4logo.png';
  const appTitle = branding.company_name || 'I4 ASSETS';
  const navigationGroups = [
    { label: 'Apps', Icon: AppWindow, sections: appsItems },
    { label: 'Assets', Icon: Box, sections: assetsSections },
    { label: 'Micro', Icon: Monitor, sections: microSections },
    { label: 'IPAM', Icon: Network, sections: ipamSections },
    { label: 'Reportes', Icon: FileSpreadsheet, sections: reportsItems },
    { label: 'Config', Icon: Settings, sections: configSections },
    ...(isAdmin ? [{ label: 'Admin', Icon: UserCog, sections: adminSections }] : []),
  ];

  return (
    <nav className="navbar sticky top-0 z-50 w-full border-b border-white/5">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          {/* Logo & Nav */}
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <img src={logoSmallUrl} alt={appTitle} className="h-7" />
              <div className="hidden sm:block">
                <span className="text-white font-semibold text-sm">{appTitle}</span>
                <span className="text-slate-500 text-xs block">{branding.login_title || 'Panel de Administración'}</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              <Link to="/">
                <Button 
                  variant="ghost" 
                  className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="h-4 w-4" strokeWidth={1.5} />
                  Dashboard
                </Button>
              </Link>
              <MegaMenu label="Apps" Icon={AppWindow} sections={appsItems} canRead={canRead} isActive={(path) => path.startsWith('/apps')} columns={1} testId="nav-apps" />
              <MegaMenu label="Assets" Icon={Box} sections={assetsSections} canRead={canRead} isActive={(path) => path.startsWith('/assets') || path.startsWith('/infra')} columns={3} testId="nav-assets" />
              <MegaMenu label="Micro" Icon={Monitor} sections={microSections} canRead={canRead} isActive={(path) => path.startsWith('/micro')} columns={3} testId="nav-micro" />
              <MegaMenu label="IPAM" Icon={Network} sections={ipamSections} canRead={canRead} isActive={(path) => path.startsWith('/ipam')} columns={1} testId="nav-ipam" />
              <MegaMenu label="Reportes" Icon={FileSpreadsheet} sections={reportsItems} canRead={canRead} isActive={(path) => path.startsWith('/reports')} columns={3} testId="nav-reportes" />
              <MegaMenu label="Config" Icon={Settings} sections={configSections} canRead={canRead} isActive={(path) => path.startsWith('/config')} columns={3} testId="nav-config" />
              {isAdmin && <MegaMenu label="Admin" Icon={UserCog} sections={adminSections} canRead={canRead} isActive={(path) => path.startsWith('/admin')} columns={1} testId="nav-admin" />}
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            <span className="hidden md:block text-sm text-slate-400 font-mono">v{APP_VERSION}</span>

            <button
              onClick={toggleTheme}
              className={`theme-toggle ${isDark ? 'dark' : ''}`}
              data-testid="theme-toggle"
            >
              <div className="theme-toggle-indicator" />
              <div className="theme-toggle-icons">
                <span>☀️</span>
                <span>🌙</span>
              </div>
            </button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-9 px-2 gap-2 text-slate-300 hover:text-white hover:bg-white/5" data-testid="user-menu">
                  <div className="h-7 w-7 rounded-md bg-primary/20 flex items-center justify-center">
                    <span className="text-primary text-xs font-medium">
                      {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm">{user?.full_name || user?.username}</span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48 bg-slate-800 border-slate-700/50" align="end">
                <div className="px-3 py-2 border-b border-slate-700/50">
                  <p className="text-sm font-medium text-white">{user?.full_name || user?.username}</p>
                  <p className="text-xs text-slate-400">{user?.email}</p>
                  {user?.role?.name && (
                    <p className="text-xs text-primary mt-1">{user.role.name}</p>
                  )}
                </div>
                {isAdmin && (
                  <div className="px-3 py-1.5 bg-primary/10 border-b border-slate-700/50">
                    <span className="text-xs text-primary font-medium">Administrador</span>
                  </div>
                )}
                <DropdownMenuItem asChild>
                  <Link 
                    to="/profile" 
                    className="flex items-center gap-2 px-3 py-2 text-slate-300 hover:text-white hover:bg-white/5 cursor-pointer"
                    data-testid="profile-btn"
                  >
                    <User className="h-4 w-4" />
                    Mi Perfil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700/50" />
                <DropdownMenuItem 
                  onClick={logout}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer m-1"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <MobileNavigation groups={navigationGroups} canRead={canRead} />
          </div>
        </div>
      </div>
    </nav>
  );
};
